import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { ROLE_LABELS, ROLES, type UserSummary } from '@hch/shared';
import { Button } from '~/components/ui/button';
import {
  Badge,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/primitives';
import { LoadingState, PageHeader } from '~/components/shared/states';
import { api } from '~/lib/api';
import { queryKeys, useCrudMutation } from '~/lib/queries';

export function AdminUsersPage() {
  const [role, setRole] = React.useState('');
  const [search, setSearch] = React.useState('');

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.users({ role, search }),
    queryFn: () =>
      api.get<UserSummary[]>('/users', { role: role || undefined, search: search || undefined }),
  });

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [form, setForm] = React.useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    password: '',
    role: 'TECHNICIAN',
  });

  const create = useCrudMutation({
    request: (input: Record<string, unknown>) => api.post('/users', input),
    invalidate: [['users']],
    successMessage: 'Utilisateur cree.',
  });

  return (
    <div className="app-page">
      <PageHeader
        title="Utilisateurs"
        description="Clients, techniciens et administrateurs."
        actions={
          <Button onClick={() => setDialogOpen(true)}>
            <Plus aria-hidden />
            Nouvel utilisateur
          </Button>
        }
      />

      <div className="flex flex-wrap gap-2">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un nom ou un e-mail…"
          className="max-w-xs"
          aria-label="Rechercher"
        />
        <Select value={role || 'all'} onValueChange={(v) => setRole(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Tous les roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les roles</SelectItem>
            {ROLES.map((item) => (
              <SelectItem key={item} value={item}>
                {ROLE_LABELS[item]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? <LoadingState /> : null}

      {data && data.length > 0 ? (
        <Card className="py-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Ville</TableHead>
                <TableHead className="text-right">Interventions</TableHead>
                <TableHead>Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">
                    {user.firstName} {user.lastName}
                    {user.isGuest ? (
                      <Badge variant="outline" className="ml-2">
                        Invite
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-xs">
                    {user.email}
                    {user.phone ? (
                      <>
                        <br />
                        {user.phone}
                      </>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Badge variant={user.role === 'ADMIN' ? 'default' : 'secondary'}>
                      {ROLE_LABELS[user.role as never]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{user.city ?? '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {user.interventionCount}
                  </TableCell>
                  <TableCell>
                    <Badge variant={user.isActive ? 'success' : 'destructive'}>
                      {user.isActive ? 'Actif' : 'Desactive'}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : null}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvel utilisateur</DialogTitle>
          </DialogHeader>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Prenom" htmlFor="firstName" required>
              <Input
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
              />
            </Field>
            <Field label="Nom" htmlFor="lastName" required>
              <Input
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
              />
            </Field>
            <Field label="Adresse e-mail" htmlFor="email" required className="sm:col-span-2">
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </Field>
            <Field label="Telephone" htmlFor="phone">
              <Input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </Field>
            <Field label="Role" htmlFor="role" required>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger id="role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((item) => (
                    <SelectItem key={item} value={item}>
                      {ROLE_LABELS[item]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field
              label="Mot de passe initial"
              htmlFor="password"
              required
              className="sm:col-span-2"
              hint="12 caracteres minimum, avec majuscule, minuscule, chiffre et caractere special."
            >
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </Field>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              loading={create.isPending}
              onClick={async () => {
                await create.mutateAsync({ ...form, phone: form.phone || null, isActive: true });
                setDialogOpen(false);
                setForm({
                  email: '',
                  firstName: '',
                  lastName: '',
                  phone: '',
                  password: '',
                  role: 'TECHNICIAN',
                });
              }}
            >
              Creer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Catalogue
